import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./Scheduled.module.sass";
import Card from "../../../components/Card";
import Form from "../../../components/Form";
import Table from "./Table";
import Panel from "./Panel";

// Services
import { getCompanyContracts } from "../../../services/contract.service";

const ContractAndConvention = ({ companyId }) => {
    const [search, setSearch] = useState("");
    const [contracts, setContracts] = useState([]);
    const [filteredContracts, setFilteredContracts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedContracts, setSelectedContracts] = useState([]);

    useEffect(() => {
        const fetchContracts = async () => {
            try {
                setLoading(true);
                const result = await getCompanyContracts(companyId);
                
                // Transformer les contrats pour correspondre au format attendu par le composant Table
                const formattedContracts = result.items.map(contract => ({
                    id: contract.id,
                    product: contract.expand?.student_id ? `${contract.expand.student_id.first_name} ${contract.expand.student_id.last_name}` : 'Étudiant non spécifié',
                    link: contract.cerfa_signature_status || 'En attente',
                    date: new Date(contract.start_date_of_contract || contract.created).toLocaleDateString(),
                    price: contract.opco_total_funded_amount || 0,
                    status: contract.opco_status || 'En attente',
                    originalContract: contract // Garder les données originales pour référence
                }));
                
                setContracts(formattedContracts);
                setFilteredContracts(formattedContracts);
            } catch (error) {
                console.error('Erreur lors de la récupération des contrats:', error);
            } finally {
                setLoading(false);
            }
        };

        if (companyId) {
            fetchContracts();
        }
    }, [companyId]);

    // Fonction pour filtrer les contrats en fonction du texte de recherche
    const filterContracts = (searchText) => {
        if (!searchText.trim()) {
            // Si la recherche est vide, afficher tous les contrats
            setFilteredContracts(contracts);
            return;
        }
        
        const searchLower = searchText.toLowerCase();
        const filtered = contracts.filter(contract => {
            // Recherche dans le nom de l'étudiant (product)
            const nameMatch = contract.product.toLowerCase().includes(searchLower);
            
            // Recherche dans les données originales du contrat
            const studentFirstName = contract.originalContract?.expand?.student_id?.first_name?.toLowerCase() || '';
            const studentLastName = contract.originalContract?.expand?.student_id?.last_name?.toLowerCase() || '';
            const companyName = contract.originalContract?.expand?.company_id?.name?.toLowerCase() || '';
            const opcoName = contract.originalContract?.opco_name?.toLowerCase() || '';
            
            return nameMatch || 
                   studentFirstName.includes(searchLower) || 
                   studentLastName.includes(searchLower) || 
                   companyName.includes(searchLower) || 
                   opcoName.includes(searchLower);
        });
        
        setFilteredContracts(filtered);
    };
    
    // Effet pour filtrer les contrats lorsque la recherche change
    useEffect(() => {
        filterContracts(search);
    }, [search, contracts]);
    
    // Gestion de la sélection des contrats
    const handleSelectContract = (contractId, isSelected) => {
        if (isSelected) {
            // Ajouter le contrat à la sélection
            const contractToAdd = contracts.find(c => c.id === contractId);
            if (contractToAdd) {
                setSelectedContracts([...selectedContracts, contractToAdd.originalContract]);
            }
        } else {
            // Retirer le contrat de la sélection
            setSelectedContracts(selectedContracts.filter(c => c.id !== contractId));
        }
    };
    
    // Sélectionner ou désélectionner tous les contrats
    const handleSelectAll = (isSelected) => {
        if (isSelected) {
            // Sélectionner tous les contrats filtrés
            const allOriginalContracts = filteredContracts.map(c => c.originalContract);
            setSelectedContracts(allOriginalContracts);
        } else {
            // Désélectionner tous les contrats
            setSelectedContracts([]);
        }
    };
    
    // Effacer la sélection
    const handleClearSelection = () => {
        setSelectedContracts([]);
    };
    
    const handleSubmit = (e) => {
        if (e) e.preventDefault();
        // Assurons-nous que la recherche fonctionne correctement
        filterContracts(search);
    };

    return (
        <>
            <Card
                className={styles.card}
                classCardHead={styles.head}
                title="Contracts"
                classTitle={cn("title-purple", styles.title)}
                head={
                    <Form
                        className={styles.form}
                        value={search}
                        setValue={setSearch}
                        onSubmit={handleSubmit}
                        placeholder="Rechercher par nom, prénom, email"
                        type="text"
                        name="search"
                        icon="search"
                    />
                }
            >
                <div className={styles.wrapper}>
                    {loading ? (
                        <div className={styles.loading}>Chargement des contrats...</div>
                    ) : filteredContracts.length > 0 ? (
                        <Table 
                            items={filteredContracts} 
                            title="Contrats de l'entreprise" 
                            selectedContracts={selectedContracts.map(c => c.id)}
                            onSelectContract={handleSelectContract}
                            onSelectAll={handleSelectAll}
                        />
                    ) : contracts.length > 0 ? (
                        <div className={styles.empty}>Aucun contrat ne correspond à votre recherche</div>
                    ) : (
                        <div className={styles.empty}>Aucun contrat trouvé pour cette entreprise</div>
                    )}
                </div>
            </Card>
            <Panel 
                selectedContracts={selectedContracts} 
                onClearSelection={handleClearSelection} 
            />
        </>
    );
};

export default ContractAndConvention;
